/**
 * Marketing sections under the hero. Content mirrors §3 of the master guide:
 * every claim in the compare table corresponds to a documented countermeasure.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

const FEATURES = [
  {
    kind: 'trust',
    kicker: 'VALIDATED CHANGES',
    title: 'Changes you can trust',
    body: 'Piixel plans each update, changes only what is needed, and verifies the result before it becomes your new working version.',
  },
  {
    kind: 'credits',
    kicker: 'RESULT-BASED CREDITS',
    title: 'Pay for working results',
    body: 'If a build fails validation, Piixel restores the last working version and uses no credits.',
  },
  {
    kind: 'ownership',
    kicker: 'FULL OWNERSHIP',
    title: 'Your app. Your code.',
    body: 'Keep complete version history and export the full source code whenever you want—ready to host or extend anywhere.',
  },
] as const;

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="m3.25 8.25 2.8 2.8 6.7-6.7" />
    </svg>
  );
}

function FeatureVisual({ kind }: { kind: typeof FEATURES[number]['kind'] }) {
  if (kind === 'trust') {
    return (
      <div className="feature-validation" aria-hidden="true">
        <div className="feature-visual-bar">
          <span className="feature-window-dots"><i /><i /><i /></span>
          <span className="feature-visual-title">project update</span>
          <span className="feature-verified"><CheckIcon /> Verified</span>
        </div>
        <div className="feature-change-row">
          <span className="feature-change-mark"><CheckIcon /></span>
          <span className="feature-change-copy">
            <small>Latest change</small>
            <strong>Ready to use</strong>
          </span>
          <span className="feature-state">Passed</span>
        </div>
        <div className="feature-stages">
          <span><CheckIcon /> Planned</span><i />
          <span><CheckIcon /> Built</span><i />
          <span><CheckIcon /> Reviewed</span>
        </div>
        <div className="feature-diff" aria-hidden="true">
          <span /><span /><span /><span />
        </div>
      </div>
    );
  }

  if (kind === 'credits') {
    return (
      <div className="feature-credit-visual" aria-hidden="true">
        <div className="feature-credit-top">
          <span>Failed build</span>
          <span className="feature-restored"><CheckIcon /> Restored</span>
        </div>
        <strong><span>0</span> credits used</strong>
        <div className="feature-recovery">
          <svg viewBox="0 0 18 18"><path d="M4.2 5.1A6 6 0 1 1 3 10.7M4.2 5.1V1.9m0 3.2H1" /></svg>
          Last working version recovered
        </div>
      </div>
    );
  }

  return (
    <div className="feature-export-visual" aria-hidden="true">
      <div className="feature-export-head">
        <span className="feature-folder">
          <svg viewBox="0 0 18 18"><path d="M2.5 4.25h4l1.4 1.5h7.6v8H2.5z" /></svg>
        </span>
        <strong>my-project</strong>
        <span className="feature-ready">Ready</span>
      </div>
      <div className="feature-file-row"><span>src</span><i /></div>
      <div className="feature-file-row"><span>public</span><i /></div>
      <div className="feature-export-status">
        <svg viewBox="0 0 18 18"><path d="M9 2.5v8m-3-3 3 3 3-3M3 12.5v2h12v-2" /></svg>
        Full source ready to export
      </div>
    </div>
  );
}

const STANDARD_STEPS = [
  { num: '01', title: 'Understand', body: 'Reads the existing project' },
  { num: '02', title: 'Change carefully', body: 'Updates only what is needed' },
  { num: '03', title: 'Verify', body: 'Checks the code and working app' },
  { num: '04', title: 'Try the experience', body: 'Opens it on desktop and mobile' },
] as const;

const STANDARD_PROMISES = [
  {
    num: '01',
    title: 'Your project comes first',
    body: 'Piixel understands the existing app before making a change.',
  },
  {
    num: '02',
    title: 'Working code stays protected',
    body: 'Every result is verified or the last working version is restored.',
  },
  {
    num: '03',
    title: 'You stay in control',
    body: 'Complete version history and full source-code export remain yours.',
  },
] as const;

const BUILD_EXAMPLES = [
  {
    id: 'portal',
    label: 'Client portal',
    category: 'Services',
    prompt: 'Build a client portal for a creative studio',
    brand: 'Northstar',
    eyebrow: 'CLIENT WORKSPACE',
    title: 'Everything your clients need, in one place.',
    body: 'Projects, approvals, invoices, and conversations stay clear from kickoff to delivery.',
    action: 'New project',
    stats: [['Active projects', '12'], ['Unread messages', '8'], ['Due this month', '€4,800']],
    panelTitle: 'Project progress',
    panelValue: '82%',
    sideTitle: 'Recent activity',
    rows: ['Homepage approved', 'Invoice #1042 paid', 'New files uploaded'],
  },
  {
    id: 'booking',
    label: 'Booking platform',
    category: 'Wellness',
    prompt: 'Build a booking app for a wellness studio',
    brand: 'Halo Studio',
    eyebrow: 'BOOK A SESSION',
    title: 'Make time for feeling better.',
    body: 'Explore services, choose a specialist, and reserve the right time in a few simple steps.',
    action: 'Book a session',
    stats: [['Open slots', '18'], ['Bookings', '43'], ['Repeat clients', '68%']],
    panelTitle: 'July availability',
    panelValue: '18 open',
    sideTitle: 'Popular sessions',
    rows: ['Deep reset · 60 min', 'Recovery flow · 45 min', 'Focus session · 30 min'],
  },
  {
    id: 'analytics',
    label: 'Analytics dashboard',
    category: 'SaaS',
    prompt: 'Build an analytics dashboard for a subscription product',
    brand: 'Current',
    eyebrow: 'GROWTH OVERVIEW',
    title: 'The numbers that move your product.',
    body: 'Revenue, activation, and retention are organized into a focused operating view.',
    action: 'Create report',
    stats: [['Monthly revenue', '€42.8k'], ['Active users', '2,418'], ['Growth', '+18.4%']],
    panelTitle: 'Revenue trend',
    panelValue: '+18.4%',
    sideTitle: 'Top channels',
    rows: ['Organic search · 41%', 'Direct · 32%', 'Referrals · 19%'],
  },
] as const;

const FAQS = [
  {
    question: 'What can I build with Piixel?',
    answer: 'You can build production-ready web apps such as client portals, dashboards, booking platforms, marketplaces, internal tools, and landing pages from a plain-language description.',
  },
  {
    question: 'Do I need to know how to code?',
    answer: 'No. Describe what you want in everyday language and continue the conversation as the project takes shape. You can still inspect and export the source code whenever you want.',
  },
  {
    question: 'How does Piixel protect a working project?',
    answer: 'Piixel reads the existing project before making focused changes, validates each result, and keeps version history so a failed build can return safely to the last working version.',
  },
  {
    question: 'Do failed builds use credits?',
    answer: 'No. If a build fails validation, Piixel restores the last working version automatically and uses zero credits for that failed run.',
  },
  {
    question: 'How many credits does an AI change use?',
    answer: 'A small text or style change uses 1 credit, a normal component or focused feature uses 3, and a first build, whole page, or larger feature uses 10. Piixel decides the weight from your requested outcome before it runs. Automatic repairs never add credits.',
  },
  {
    question: 'Can I keep and export my code?',
    answer: 'Yes. Your project and its version history stay under your control, and you can export the complete source code when you are ready to host or extend it elsewhere.',
  },
  {
    question: 'Can I edit and create components without using a prompt?',
    answer: 'Yes. Select elements directly in the live app to change, move, duplicate, or delete them. You can insert new elements, build custom layouts, and turn a selection into a reusable component. Visual edits use no build credits.',
  },
] as const;

const EDITOR_MODES = [
  { id: 'style', label: 'Edit anything', detail: 'Text, style, size, and layout' },
  { id: 'insert', label: 'Create elements', detail: 'Ready-made or completely custom' },
  { id: 'component', label: 'Make components', detail: 'Reusable across your project' },
] as const;

const VISUAL_EDITOR_SAMPLE = {
  heading: 'Everything important, finally together.',
  body: 'A flexible workspace your team can make its own.',
  action: 'Explore workspace',
} as const;

const EDITOR_INSERTS = [
  { label: 'Heading', icon: 'heading' },
  { label: 'Text', icon: 'text' },
  { label: 'Button', icon: 'button' },
  { label: 'Image', icon: 'image' },
  { label: 'Card', icon: 'card' },
  { label: 'Section', icon: 'section' },
  { label: 'Form', icon: 'form' },
  { label: 'Custom', icon: 'custom' },
] as const;

function EditorInsertIcon({ name }: { name: typeof EDITOR_INSERTS[number]['icon'] }) {
  const paths = {
    heading: <><path d="M3 4h14M10 4v12M6.5 16h7" /></>,
    text: <><path d="M4 5h12M4 9h12M4 13h8" /></>,
    button: <><rect x="2.75" y="5" width="14.5" height="10" rx="3" /><path d="M7 10h6" /></>,
    image: <><rect x="2.75" y="3" width="14.5" height="14" rx="2.5" /><circle cx="7" cy="7.25" r="1.25" /><path d="m4.5 14 3.4-3.5 2.4 2.2 2-2 3.2 3.3" /></>,
    card: <><rect x="2.75" y="3" width="14.5" height="14" rx="2.5" /><path d="M3 7h14M5.5 10.5h6M5.5 13.5h3.5" /></>,
    section: <><rect x="2.75" y="3" width="14.5" height="5" rx="1.5" /><rect x="2.75" y="10" width="14.5" height="7" rx="1.5" /></>,
    form: <><rect x="3" y="3" width="4" height="4" rx="1" /><path d="M9 5h8M3 10h14M3 14h10" /></>,
    custom: <><rect x="2.75" y="2.75" width="14.5" height="14.5" rx="3" strokeDasharray="2.4 2.4" /><path d="M10 6.5v7M6.5 10h7" /></>,
  } as const;

  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function ExamplePreview({ example }: { example: typeof BUILD_EXAMPLES[number] }) {
  return (
    <div className={`experience-app experience-app--${example.id}`} aria-hidden="true">
      <div className="experience-app-bar">
        <span className="experience-app-brand"><i />{example.brand}</span>
        <span className="experience-app-nav">Overview&nbsp;&nbsp;&nbsp;Projects&nbsp;&nbsp;&nbsp;People</span>
        <span className="experience-app-avatar">AM</span>
      </div>
      <div className="experience-app-body">
        <div className="experience-app-hero">
          <div>
            <span>{example.eyebrow}</span>
            <h3>{example.title}</h3>
            <p>{example.body}</p>
          </div>
          <span className="experience-app-action">{example.action}</span>
        </div>
        <div className="experience-app-stats">
          {example.stats.map(([label, value]) => (
            <span key={label}><small>{label}</small><strong>{value}</strong></span>
          ))}
        </div>
        <div className="experience-app-panels">
          <div className="experience-app-chart">
            <div className="experience-app-panel-head">
              <span>{example.panelTitle}</span>
              <strong>{example.panelValue}</strong>
            </div>
            {example.id === 'booking' ? (
              <div className="experience-calendar">
                {Array.from({ length: 21 }, (_, index) => <i key={index} />)}
              </div>
            ) : (
              <div className="experience-bars">
                {Array.from({ length: 10 }, (_, index) => <i key={index} />)}
              </div>
            )}
          </div>
          <div className="experience-app-list">
            <div className="experience-app-panel-head"><span>{example.sideTitle}</span><small>Live</small></div>
            {example.rows.map((row, index) => (
              <div className="experience-app-row" key={row}>
                <i>{String(index + 1).padStart(2, '0')}</i><span>{row}</span><b>↗</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VisualEditorPanel({ mode }: { mode: typeof EDITOR_MODES[number]['id'] }) {
  if (mode === 'insert') {
    return (
      <div className="visual-panel-body visual-panel-insert" aria-hidden="true">
        <span className="visual-panel-label">INSERT ELEMENT</span>
        <div className="visual-palette">
          {EDITOR_INSERTS.map((item) => (
            <span className={item.label === 'Custom' ? 'is-custom' : ''} key={item.label}>
              <EditorInsertIcon name={item.icon} />{item.label}
            </span>
          ))}
        </div>
        <div className="visual-custom-callout">
          <span className="visual-custom-icon"><i /><i /><i /></span>
          <span><strong>Start completely custom</strong><small>Create your own element from a blank container.</small></span>
        </div>
        <div className="visual-insert-position">
          <small>Insert position</small><span>Before</span><span className="is-active">Inside</span><span>After</span>
        </div>
      </div>
    );
  }

  if (mode === 'component') {
    return (
      <div className="visual-panel-body visual-panel-component" aria-hidden="true">
        <span className="visual-panel-label">MAKE COMPONENT</span>
        <p>Turn the selected elements into one reusable component.</p>
        <div className="visual-selection-chips"><span>Feature card</span><span>Primary button</span></div>
        <label>Component name</label>
        <div className="visual-component-name">FeatureSpotlight</div>
        <div className="visual-component-result">
          <span><i /> Component ready</span>
          <strong>FeatureSpotlight.tsx</strong>
          <small>Reusable anywhere in this project</small>
        </div>
        <span className="visual-create-button">Create component</span>
      </div>
    );
  }

  return (
    <div className="visual-panel-body visual-panel-style" aria-hidden="true">
      <span className="visual-panel-label">STYLE</span>
      <div className="visual-control-row"><span>Text</span><strong>{VISUAL_EDITOR_SAMPLE.heading}</strong></div>
      <div className="visual-control-row visual-control-inline">
        <span>Text style</span><strong>Archivo</strong><strong>32 px</strong><strong>700</strong>
      </div>
      <div className="visual-control-row visual-color-row">
        <span>Color</span><i /><i /><i className="is-active" /><i /><small>#172033</small>
      </div>
      <div className="visual-control-row visual-space-row">
        <span>Spacing</span><strong>24</strong><strong>32</strong><strong>24</strong><strong>32</strong>
      </div>
      <div className="visual-control-row visual-align-row">
        <span>Align</span><strong>≡</strong><strong className="is-active">≡</strong><strong>≡</strong>
      </div>
    </div>
  );
}

/* Mirrors the backend single-source-of-truth (piixel-helix-api src/lib/plans.ts).
   Prices are VAT-INCLUSIVE (Stripe Tax, tax_behavior=inclusive). `plan` carries the
   chosen tier through signup → dashboard auto-checkout; null = Free. Team is the
   only unreleased tier and stays visibly unavailable until collaboration launches. */
const TIERS = [
  {
    name: 'Free', audience: 'For exploring and early products', price: '€0', cadence: '/month', featured: false, plan: null, available: true,
    summary: 'Build, refine, and publish real web apps without entering a card.',
    allowance: '30 AI credits',
    allowanceDetail: '1 small · 3 component · 10 full build',
    points: ['Create up to 10 projects', 'Unlimited visual editing', 'Version history and restore', 'Publish with the Piixel badge'],
    cta: 'Start building free', note: 'No card required',
  },
  {
    name: 'Pro', audience: 'For products ready to launch', price: '€25', cadence: '/month', featured: false, plan: 'pro', available: true,
    summary: 'Launch private, branded products with more room to build and ship.',
    allowance: '150 AI credits',
    allowanceDetail: '1 small · 3 component · 10 full build',
    points: ['Unlimited projects', 'Private apps and publishing', 'Custom domain and no badge', 'Source-code export to GitHub'],
    cta: 'Choose Pro', note: 'The complete launch toolkit',
  },
  {
    name: 'Max', audience: 'Best value for growing products', price: '€49', cadence: '/month', featured: true, plan: 'max', available: true,
    summary: 'More than double Pro capacity, advanced control, and priority help at a predictable price.',
    allowance: '400 AI credits',
    allowanceDetail: '1 small · 3 component · 10 full build',
    points: ['Everything in Pro', 'Fair-use hosting and media', 'White-label, API, and advanced security', 'Priority support'],
    cta: 'Choose Max', note: 'No automatic overage charges',
  },
  {
    name: 'Team', audience: 'For teams building together', price: '€99', cadence: '/month', featured: false, plan: 'team', available: false,
    summary: 'A shared workspace for collaborative building and central management.',
    allowance: '900 shared AI credits',
    allowanceDetail: '1 small · 3 component · 10 full build',
    points: ['Everything in Max', 'Shared projects and collaboration', 'Team roles and permissions', 'Central billing and usage'],
    cta: 'Coming soon', note: 'Expected €99/month at launch',
  },
] as const;

export default function Sections() {
  const [activeExampleIndex, setActiveExampleIndex] = useState(0);
  const activeExample = BUILD_EXAMPLES[activeExampleIndex] ?? BUILD_EXAMPLES[0];
  const [editorMode, setEditorMode] = useState<typeof EDITOR_MODES[number]['id']>('style');

  return (
    <>
      <section className="section feature-section" id="features">
        <div className="feature-intro">
          <div>
            <p className="section-eyebrow">WHY PIIXEL</p>
            <h2 className="section-title">Build with confidence. Keep what you create.</h2>
          </div>
          <p className="section-lede">
            Piixel is an AI app builder for production-ready web apps. Every change is validated,
            every version can be restored, and the complete source code stays yours.
          </p>
        </div>
        <div className="feature-showcase">
          {FEATURES.map((f) => (
            <article className={`feature-panel feature-panel--${f.kind}`} key={f.kind}>
              <div className="feature-panel-copy">
                <span className="feature-kicker">{f.kicker}</span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
              <FeatureVisual kind={f.kind} />
            </article>
          ))}
        </div>
      </section>

      <section className="section standard-section" id="compare">
        <div className="standard-intro">
          <div>
            <p className="section-eyebrow">THE PIIXEL STANDARD</p>
            <h2 className="section-title">What happens after you press Build matters.</h2>
          </div>
          <p className="section-lede">
            Piixel does more than generate code. It protects the working product you already have,
            then opens the finished result in a real browser before it becomes your new version.
          </p>
        </div>

        <div className="standard-console">
          <div className="standard-console-head">
            <span className="standard-console-name">
              <span className="standard-console-dots"><i /><i /><i /></span>
              PIIXEL BUILD 024
            </span>
            <span className="standard-protected">
              <svg viewBox="0 0 18 18" aria-hidden="true"><path d="M9 2.2 14.2 4v4.1c0 3.2-2.2 5.9-5.2 7.5-3-1.6-5.2-4.3-5.2-7.5V4z" /><path d="m6.4 8.8 1.7 1.7 3.5-3.6" /></svg>
              Protected build
            </span>
          </div>

          <div className="standard-request">
            <span>Your request</span>
            <strong>Add subscription billing to my app</strong>
            <span className="standard-request-state">In progress</span>
          </div>

          <div className="standard-flow" aria-label="How Piixel processes a build request">
            {STANDARD_STEPS.map((step) => (
              <div className="standard-step" key={step.num}>
                <span className="standard-step-num">{step.num}</span>
                <span className="standard-step-copy">
                  <strong>{step.title}</strong>
                  <small>{step.body}</small>
                </span>
                <span className="standard-step-done"><CheckIcon /> Done</span>
              </div>
            ))}
          </div>

          <div className="standard-outcomes">
            <span className="standard-outcomes-label">Verification result</span>
            <div className="standard-outcome standard-outcome--passed">
              <span className="standard-outcome-badge"><CheckIcon /> Passed</span>
              <strong>New working version</strong>
              <small>Code, desktop, mobile, links, and controls have been checked.</small>
            </div>
            <div className="standard-outcome standard-outcome--restored">
              <span className="standard-outcome-badge">
                <svg viewBox="0 0 18 18" aria-hidden="true"><path d="M4.2 5.1A6 6 0 1 1 3 10.7M4.2 5.1V1.9m0 3.2H1" /></svg>
                Failed safely
              </span>
              <strong>Restored automatically</strong>
              <small>Last working version · 0 credits used</small>
            </div>
          </div>
        </div>

        <div className="standard-promises">
          {STANDARD_PROMISES.map((promise) => (
            <article className="standard-promise" key={promise.num}>
              <span>{promise.num}</span>
              <div>
                <h3>{promise.title}</h3>
                <p>{promise.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section experience-section" id="showcase">
        <div className="experience-intro">
          <div>
            <p className="section-eyebrow">WHAT WILL YOU BUILD?</p>
            <h2 className="section-title">Start with an idea. Leave with a working product.</h2>
          </div>
          <p className="section-lede">
            Turn a plain-language prompt into a polished web application. Explore a few of the
            products Piixel can help you bring to life.
          </p>
        </div>

        <div className="experience-stage">
          <div className="experience-picker" role="tablist" aria-label="Example applications">
            <span className="experience-picker-label">CHOOSE AN IDEA</span>
            {BUILD_EXAMPLES.map((example, index) => (
              <button
                type="button"
                role="tab"
                aria-selected={index === activeExampleIndex}
                aria-controls="experience-preview"
                className={index === activeExampleIndex ? 'is-active' : ''}
                onClick={() => setActiveExampleIndex(index)}
                key={example.id}
              >
                <span>{example.label}</span>
                <small>{example.category}</small>
              </button>
            ))}
            <div className="experience-prompt">
              <span>PROMPT</span>
              <p>“{activeExample.prompt}”</p>
              <i><CheckIcon /> Ready to build</i>
            </div>
          </div>

          <div
            className="experience-preview"
            id="experience-preview"
            role="tabpanel"
            aria-label={`${activeExample.label} example`}
          >
            <div className="experience-preview-head">
              <span className="experience-preview-dots"><i /><i /><i /></span>
              <span>{activeExample.id}.piixel.app</span>
              <span className="experience-preview-live"><i /> Live preview</span>
            </div>
            <ExamplePreview example={activeExample} />
          </div>
        </div>
      </section>

      <section className="section visual-editor-section" id="visual-editor">
        <div className="visual-editor-intro">
          <div>
            <p className="section-eyebrow">VISUAL EDITOR</p>
            <h2 className="section-title">Edit the app. Create what isn’t there.</h2>
          </div>
          <div>
            <p className="section-lede">
              Select anything in the live app to rewrite, restyle, resize, or rearrange it. Add new
              elements, design custom layouts, and turn your selection into a reusable component.
            </p>
            <span className="visual-editor-free"><CheckIcon /> No prompt required <i /> No credits used</span>
          </div>
        </div>

        <div className="visual-editor-demo">
          <div className="visual-editor-mode" role="tablist" aria-label="Visual editor capabilities">
            {EDITOR_MODES.map((mode, index) => (
              <button
                type="button"
                role="tab"
                aria-selected={editorMode === mode.id}
                aria-controls="visual-editor-preview"
                className={editorMode === mode.id ? 'is-active' : ''}
                onClick={() => setEditorMode(mode.id)}
                key={mode.id}
              >
                <small>{String(index + 1).padStart(2, '0')}</small>
                <span>{mode.label}</span>
                <i>{mode.detail}</i>
              </button>
            ))}
          </div>

          <div className="visual-editor-workspace" id="visual-editor-preview" role="tabpanel" aria-label={`${editorMode} visual editor example`}>
            <div className="visual-canvas">
              <div className="visual-canvas-bar">
                <span className="visual-canvas-route"><i /> northstar.app / overview</span>
                <span className="visual-canvas-viewport">Desktop&nbsp;&nbsp;·&nbsp;&nbsp;1440</span>
                <span className="visual-canvas-live"><i /> Live</span>
              </div>
              <div className="visual-canvas-page">
                <div className="visual-canvas-nav"><strong>Northstar</strong><span>Product&nbsp;&nbsp;&nbsp;Solutions&nbsp;&nbsp;&nbsp;Pricing</span><i /></div>
                <div className="visual-canvas-copy"><span>YOUR WORKSPACE</span><h3>Move faster with one workspace.</h3><p>Plan projects, share work, and keep every client in sync.</p></div>
                <div className={`visual-selected visual-selected--${editorMode}`}>
                  {editorMode !== 'style' && (
                    <>
                      <span className="visual-selection-tag">{editorMode === 'component' ? '2 elements selected' : 'Section · div'}</span>
                      <i className="visual-handle visual-handle--tl" /><i className="visual-handle visual-handle--tr" />
                      <i className="visual-handle visual-handle--bl" /><i className="visual-handle visual-handle--br" />
                    </>
                  )}
                  <span className="visual-selected-kicker">BUILT FOR FOCUS</span>
                  {editorMode === 'style' ? (
                    <strong className="visual-selected-heading">
                      <span className="visual-selection-tag">Heading · h2</span>
                      <i className="visual-handle visual-handle--tl" /><i className="visual-handle visual-handle--tr" />
                      <i className="visual-handle visual-handle--bl" /><i className="visual-handle visual-handle--br" />
                      {VISUAL_EDITOR_SAMPLE.heading}
                    </strong>
                  ) : (
                    <strong>{VISUAL_EDITOR_SAMPLE.heading}</strong>
                  )}
                  <small>{VISUAL_EDITOR_SAMPLE.body}</small>
                  <span className="visual-selected-action">{VISUAL_EDITOR_SAMPLE.action}</span>
                  {editorMode === 'component' && <span className="visual-secondary-selection" />}
                  {editorMode !== 'style' && (
                    <>
                      <span className="visual-edge-add visual-edge-add--top">+</span>
                      <span className="visual-edge-add visual-edge-add--bottom">+</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <aside className="visual-panel">
              <div className="visual-panel-head">
                <span><i /> {editorMode === 'component' ? 'Selection' : editorMode === 'insert' ? 'Add to section' : 'Heading'}</span>
                <span className="visual-panel-actions"><i>⧉</i><i>↑</i><i>×</i></span>
              </div>
              <VisualEditorPanel mode={editorMode} />
              <div className="visual-panel-foot">
                <span><CheckIcon /> Visual edit · 0 credits</span>
                <i className="visual-panel-discard">Discard</i>
                <i>Apply</i>
              </div>
            </aside>
          </div>
        </div>

        <div className="visual-editor-promises">
          <article><span>01</span><div><h3>Select anything</h3><p>Click directly on the element you want to change, or select several at once.</p></div></article>
          <article><span>02</span><div><h3>Build your own</h3><p>Insert ready-made elements or compose custom, reusable components from scratch.</p></div></article>
          <article><span>03</span><div><h3>Change safely</h3><p>Every applied edit is versioned, checked, and reversible from project history.</p></div></article>
        </div>
      </section>

      <section className="section pricing-section" id="pricing">
        <div className="pricing-intro">
          <div>
            <p className="section-eyebrow">SIMPLE PRICING</p>
            <h2 className="section-title">Start free. Scale when it matters.</h2>
          </div>
          <p className="section-lede">
            Build and publish for free, then unlock private projects, custom domains,
            source-code export, and more capacity with no surprise overage charges.
          </p>
        </div>

        <div className="pricing-assurances" aria-label="Pricing guarantees">
          <span><CheckIcon /> Failed builds never count</span>
          <span><CheckIcon /> Visual edits use no credits</span>
          <span><CheckIcon /> No automatic overages</span>
        </div>

        <div className="pricing-plans">
          {TIERS.map((t) => {
            const to = t.plan ? `/signup?plan=${t.plan}` : '/signup';
            return (
              <article className={`price-card${t.featured ? ' featured' : ''}${t.available ? '' : ' price-card--soon'}`} key={t.name}>
                <div className="price-card-topline">
                  <span>{t.audience}</span>
                  <i className={t.featured ? 'is-featured' : t.available ? 'is-live' : 'is-soon'}>
                    {t.featured ? 'MOST POPULAR' : t.available ? 'AVAILABLE NOW' : 'COMING SOON'}
                  </i>
                </div>
                <h3>{t.name}</h3>
                <p className="price-summary">{t.summary}</p>
                <p className="price-amount">{t.price}<span>{t.cadence}</span></p>
                <div className="price-usage">
                  <span>INCLUDED EACH MONTH</span>
                  <strong>{t.allowance}</strong>
                  <small>{t.allowanceDetail}</small>
                </div>
                <ul>
                  {t.points.map((p) => <li key={p}><CheckIcon />{p}</li>)}
                </ul>
                {!t.available ? (
                  <span className="btn btn-ghost" aria-disabled="true">{t.cta}</span>
                ) : (
                  <Link to={to} className={`btn ${t.featured ? 'btn-primary' : 'btn-ghost'}`}>{t.cta}</Link>
                )}
                <small className="price-card-note">{t.note}</small>
              </article>
            );
          })}
        </div>
        <p className="price-fineprint">
          Prices include VAT. Successful AI builds reset monthly; failed changes never count.
          Published traffic and media are included under fair use, and Piixel warns you before extra capacity is needed.
          By subscribing, you agree to our <Link to="/terms-of-service">Terms</Link>.
        </p>
      </section>

      <section className="section faq-section" id="faq">
        <div className="faq-intro">
          <div>
            <p className="section-eyebrow">GOOD TO KNOW</p>
            <h2 className="section-title">Questions, answered clearly.</h2>
          </div>
          <p className="section-lede">
            Everything you need to know before turning your first idea into a working web app.
          </p>
        </div>
        <div className="faq-list">
          {FAQS.map((faq, index) => (
            <details className="faq-item" key={faq.question}>
              <summary><span>{String(index + 1).padStart(2, '0')}</span>{faq.question}<i aria-hidden="true" /></summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="section final-experience">
        <div className="final-experience-glow" aria-hidden="true" />
        <p className="section-eyebrow">YOUR NEXT VERSION</p>
        <h2 className="section-title">Your idea is closer than it looks.</h2>
        <p>
          Describe the product you want. Piixel helps you build it, verify it, and keep every line.
        </p>
        <div className="final-experience-actions">
          <Link to="/signup" className="btn btn-primary">Start building — free</Link>
          <a href="/#pricing" className="btn btn-ghost">See pricing</a>
        </div>
        <div className="final-experience-notes">
          <span><CheckIcon /> No card required</span>
          <span><CheckIcon /> Failed builds use 0 credits</span>
          <span><CheckIcon /> Your code stays yours</span>
        </div>
      </section>
    </>
  );
}
